// Team Transcript Detail Component - Right column with 4 sections (clients, transcript, notes, action items)

import React, { useEffect } from 'react';
import { useTeamTranscriptDetail } from '../../hooks/useTeamTranscriptDetail';
import TeamClientsSection from './TeamClientsSection';
import TeamNotesSection from './TeamNotesSection';
import TeamActionItemsSection from './TeamActionItemsSection';
import type { 
  TeamTranscript, 
  TeamTranscriptDetail as TeamTranscriptDetailType 
} from '../../types/teamTranscripts';

interface TeamTranscriptDetailProps {
  teamId: string;
  transcript: TeamTranscript;
  transcriptDetail: TeamTranscriptDetailType | null;
  onTranscriptDetailLoaded: (detail: TeamTranscriptDetailType) => void;
  onTranscriptUpdated: () => void;
}

const TeamTranscriptDetail: React.FC<TeamTranscriptDetailProps> = ({
  teamId,
  transcript,
  transcriptDetail,
  onTranscriptDetailLoaded,
  onTranscriptUpdated
}) => {
  const {
    transcript: detailTranscript,
    clients,
    notes,
    actionItems,
    canEdit,
    loading,
    error,
    savingNotes,
    savingActionItems,
    updateNotes,
    updateActionItems,
    refresh
  } = useTeamTranscriptDetail(teamId, transcript);

  // Notify parent when transcript detail is loaded
  useEffect(() => {
    if (detailTranscript && !transcriptDetail) {
      onTranscriptDetailLoaded(detailTranscript);
    }
  }, [detailTranscript, transcriptDetail, onTranscriptDetailLoaded]);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch {
      return 'Unknown date';
    }
  };

  const handleNotesUpdated = () => {
    onTranscriptUpdated();
  };

  const handleActionItemsUpdated = () => {
    onTranscriptUpdated();
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading transcript details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-4xl mb-4">⚠️</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Details</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={refresh}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!detailTranscript) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">📝</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Details Available</h3>
          <p className="text-gray-600">Unable to load transcript details</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      {/* Header with Title and Metadata */}
      <div className="bg-white border-b border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold text-gray-900">
              {detailTranscript.title}
            </h1>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400 font-mono">
                ID: {detailTranscript.id}
              </span>
            </div>
          </div>
          <div className="text-right text-sm text-gray-500">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                📝 {detailTranscript.content.split(/\s+/).filter(word => word.length > 0).length} words
              </span>
              <span className="flex items-center gap-1">
                📊 {detailTranscript.content.length.toLocaleString()} characters
              </span>
            </div>
          </div>
        </div>
        
        {/* Owner and Date Info */}
        <div className="mt-2 flex items-center justify-between text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <span>👤 {detailTranscript.owner_name}</span>
            <span className="text-gray-400">•</span>
            <span>{detailTranscript.owner_email}</span>
          </div>
          <div>
            Created: {formatDate(detailTranscript.created_at)}
          </div>
        </div>
      </div>

      {/* Main Content - 4 Sections */}
      <div className="p-6 space-y-6">
        {/* Section 1: Connected Clients (Read-only) */}
        <TeamClientsSection 
          clients={clients}
          loading={loading}
        />

        {/* Section 2: Transcript Content (Read-only) */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            📄 Transcript Content
          </h3>
          <div className="prose prose-gray max-w-none">
            <div className="text-gray-800 leading-relaxed whitespace-pre-wrap text-base bg-gray-50 p-4 rounded-lg border">
              {detailTranscript.content}
            </div>
          </div>
        </div>

        {/* Section 3: Notes (Editable with manual save) */}
        <TeamNotesSection
          teamId={teamId}
          transcriptId={transcript.id}
          notes={notes}
          canEdit={canEdit}
          saving={savingNotes}
          onUpdateNotes={updateNotes}
          onNotesUpdated={handleNotesUpdated}
        />

        {/* Section 4: Action Items (Editable with manual save) */}
        <TeamActionItemsSection
          teamId={teamId}
          transcriptId={transcript.id}
          actionItems={actionItems}
          canEdit={canEdit}
          saving={savingActionItems}
          onUpdateActionItems={updateActionItems}
          onActionItemsUpdated={handleActionItemsUpdated}
        />
      </div>
    </div>
  );
};

export default TeamTranscriptDetail;